<template>
  <template v-if="canManage">
    <div v-if="loadingAccountCreator" class="flex justify-center py-8">
      <v-progress-circular indeterminate color="primary" width="2" size="24" />
    </div>
    <template v-else-if="accountCreator">
      <v-form
        id="account-creator-form"
        ref="accountCreatorForm"
        :disabled="accountCreatorRelatedBusy || !canEditKyc"
        class="flex flex-col gap-6"
        validate-on="invalid-input lazy"
        @submit.prevent="save"
      >
        <OpeningCard :title="t('treezorOnboarding.accountCreator.sections.identity')">
          <PersonIdentityFields
            v-model="accountCreatorFormData"
            :missing-fields="showMissing ? accountCreatorMissingFields : []"
          >
            <v-col cols="12">
              <div class="text-medium-emphasis">
                {{ t("treezorOnboarding.accountCreator.specifiedUSPersonQuestion") }}
                <span class="text-error">*</span>
              </div>
              <v-radio-group
                v-model="accountCreatorFormData.specifiedUSPerson"
                color="primary"
                inline
                hide-details="auto"
                :aria-label="t('treezorOnboarding.accountCreator.specifiedUSPersonQuestion')"
              >
                <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="true" />
                <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="false" />
              </v-radio-group>
            </v-col>
          </PersonIdentityFields>
        </OpeningCard>
        <OpeningCard :title="t('treezorOnboarding.sections.contact')">
          <PersonContactFields
            v-model="accountCreatorFormData"
            :missing-fields="showMissing ? accountCreatorMissingFields : []"
          />
        </OpeningCard>
        <OpeningCard :title="t('treezorOnboarding.accountCreator.sections.role')">
          <v-radio-group
            v-model="accountCreatorFormData.legalRole"
            hide-details
            color="primary"
            class="[&>*>.v-label]:ms-0"
          >
            <template #label>
              {{ t("treezorOnboarding.accountCreator.legalRoleQuestion") }}
              <span class="text-error">*</span>
            </template>
            <v-radio
              v-for="legalRole in ['legalRepresentative', 'companyRepresentative']"
              :key="legalRole"
              :value="legalRole"
              class="mb-2"
            >
              <template #label>
                <div class="flex flex-col">
                  <span>{{ t(`treezorOnboarding.accountCreator.legalRoles.${legalRole}`) }}</span>
                  <span class="text-xs text-medium-emphasis">
                    {{ t(`treezorOnboarding.accountCreator.legalRoleHints.${legalRole}`) }}
                  </span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <p
            v-if="accountCreatorFormData.legalRole === 'companyRepresentative'"
            class="text-xs text-medium-emphasis mt-2 mb-0"
          >
            {{ t("treezorOnboarding.accountCreator.legalRepresentativeHint") }}
          </p>
          <div class="text-medium-emphasis mt-4">
            {{ t("treezorOnboarding.accountCreator.isEffectiveBeneficiaryQuestion") }}
            <span class="text-error">*</span>
          </div>
          <p class="text-xs text-medium-emphasis m-0">
            {{ t("treezorOnboarding.accountCreator.isEffectiveBeneficiaryHint") }}
          </p>
          <v-radio-group
            v-model="accountCreatorFormData.isEffectiveBeneficiary"
            color="primary"
            inline
            hide-details="auto"
            :aria-label="t('treezorOnboarding.accountCreator.isEffectiveBeneficiaryQuestion')"
          >
            <v-radio :label="t('treezorOnboarding.sanctions.yes')" :value="true" />
            <v-radio :label="t('treezorOnboarding.sanctions.no')" :value="false" />
          </v-radio-group>
        </OpeningCard>
      </v-form>
    </template>

    <div v-if="!loadingAccountCreator" class="mt-6 flex justify-end gap-2">
      <v-btn
        class="me-auto"
        variant="text"
        prepend-icon="mdi-chevron-left"
        :disabled="accountCreatorRelatedBusy"
        @click="emit('previous')"
      >
        {{ t("common.previous") }}
      </v-btn>
      <v-btn
        v-if="accountCreator"
        type="submit"
        form="account-creator-form"
        color="primary"
        variant="tonal"
        :loading="updatingAccountCreator"
        :disabled="accountCreatorRelatedBusy || !canEditKyc"
      >
        {{ t("treezorOnboarding.saveDraft") }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-content-save"
        append-icon="mdi-chevron-right"
        :disabled="accountCreatorRelatedBusy"
        @click="onNext"
      >
        {{ t("common.next") }}
      </v-btn>
    </div>
  </template>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { captureException } from "@sentry/vue";
import { ref } from "vue";
import type { VForm } from "vuetify/components";
import OpeningCard from "../OpeningCard.vue";
import PersonContactFields from "../PersonContactFields.vue";
import PersonIdentityFields from "../PersonIdentityFields.vue";
import { accountCreatorFormFrom, useTreezorOnboarding } from "../useTreezorOnboarding";

defineProps<{ showMissing: boolean }>();
const emit = defineEmits<{ previous: []; next: [accountId: string] }>();

const { t } = useI18n();
const { showSuccessToast, showErrorToast } = useToast();
const {
  accountId,
  canManage,
  canEditKyc,
  accountCreator,
  accountCreatorRelationship,
  loadingAccountCreator,
  updatingAccountCreator,
  accountCreatorFormData,
  accountCreatorRelatedBusy,
  accountCreatorMissingFields,
  loadEffectiveBeneficiaries,
} = useTreezorOnboarding();

const accountCreatorForm = ref<VForm>();

const save = async () => {
  if (!canEditKyc.value || !accountCreator.value) return true;
  const result = await accountCreatorForm.value?.validate();
  if (!result?.valid) return false;

  updatingAccountCreator.value = true;
  try {
    const updated = await call("updateTreezorAccountCreator")({
      accountId: accountId.value,
      specifiedUSPerson: accountCreatorFormData.specifiedUSPerson,
      firstname: accountCreatorFormData.firstname,
      lastname: accountCreatorFormData.lastname,
      middleNames: accountCreatorFormData.middleNames,
      birthday: accountCreatorFormData.birthday,
      placeOfBirth: accountCreatorFormData.placeOfBirth,
      birthCountry: accountCreatorFormData.birthCountry ?? "",
      nationality: accountCreatorFormData.nationality ?? "",
      phone: accountCreatorFormData.phone,
      address1: accountCreatorFormData.address1,
      address2: accountCreatorFormData.address2,
      address3: accountCreatorFormData.address3,
      postcode: accountCreatorFormData.postcode,
      city: accountCreatorFormData.city,
      country: accountCreatorFormData.country ?? "",
      legalRole: accountCreatorFormData.legalRole,
      isEffectiveBeneficiary: accountCreatorFormData.isEffectiveBeneficiary,
    });
    accountCreator.value = updated.user;
    accountCreatorRelationship.value = updated.relationship;
    Object.assign(accountCreatorFormData, accountCreatorFormFrom(updated.user, updated));
    showSuccessToast(t("treezorOnboarding.accountCreator.updateSuccess"));
    await loadEffectiveBeneficiaries({
      silent: true,
      replaceUserIds: [updated.user.userId],
    });
    return true;
  } catch (cause) {
    const error = new Error("Error updating Treezor account creator", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(cause instanceof Error ? cause.message : undefined);
    return false;
  } finally {
    updatingAccountCreator.value = false;
  }
};

const onNext = async () => {
  const savedAccountId = accountId.value;
  if (await save()) emit("next", savedAccountId);
};
</script>
